import { FileTextOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Space, message, theme } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { PageHeader } from 'components/headings';
import { useBudgetList } from 'hooks/useApi/useBudgetApi';
import { getBudgetSummaryApi, getOrderWithItemsApi } from 'api/hallazgoFlujo';
import { useActionsODT } from 'providers/ActionsOdtProvider';
import BudgetToolbar from 'components/admin/Budget/BudgetToolbar';
import BudgetTable from 'components/admin/Budget/BudgetTable/BudgetTable';
import BudgetApprovalModal from 'components/admin/Budget/BudgetApprovalModal';
import BudgetKpiStrip from 'components/admin/Budget/BudgetKpiStrip';
import BudgetEditorDrawer from 'components/admin/Budget/BudgetEditor/BudgetEditorDrawer';
import GenerateOrderDrawer from 'components/admin/ODT/GenerateOrderDrawer';
import PlanificarOdtModal from 'components/admin/ODT/PlanificarOdtModal';
import OrderStatus from 'components/admin/Budget/OrderStatus/OrderStatus';
import { Modal } from 'antd';
import paths from 'assets/routes/paths';
import SectionHint from 'components/ciclo/SectionHint';
import CicloEmpty from 'components/ciclo/CicloEmpty';
import { useNavigate, useSearchParams } from 'react-router-dom';

const { useToken } = theme;

const EMPTY_ORDER_DRAWER = {
  open: false,
  budget: null,
  idClient: null,
  hallazgos: [],
  presupuestoItems: [],
  existingOrder: null,
};

function Budget() {
  const { token } = useToken();
  const { dowload: downloadOdt } = useActionsODT();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState(() => ({
    // Deep-link desde la bandeja del Home (/admin/budget?stage=aprobado_sin_odt):
    // pre-aplica el filtro de etapa al montar.
    stage: (searchParams.get('stage') || '').split(',').filter(Boolean),
    search: '',
    idClient: null,
    commune: '',
  }));

  const listParams = useMemo(() => ({ page, pageSize, ...filters }), [page, pageSize, filters]);
  const { rows, total, loading, refetch } = useBudgetList(listParams);
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpAndRefetch = useCallback(() => {
    setRefreshKey((k) => k + 1);
    refetch();
  }, [refetch]);

  const [editDrawer, setEditDrawer] = useState({ open: false, budget: null });
  const [approvalTarget, setApprovalTarget] = useState(null);
  const [orderDrawer, setOrderDrawer] = useState(EMPTY_ORDER_DRAWER);
  const [statusModal, setStatusModal] = useState({ open: false, budget: null });
  // Oferta de planificar la ODT recién creada en la carta Gantt. `idOrder` se conserva
  // al cerrar para que el título no pase a "#null" durante la animación de salida.
  const [plan, setPlan] = useState({ open: false, idOrder: null });

  const handleFiltersChange = useCallback((next) => {
    setFilters(next);
    setPage(1);
  }, []);

  const handlePageChange = useCallback(
    (nextPage, nextPageSize) => {
      setPage(nextPage);
      if (nextPageSize && nextPageSize !== pageSize) {
        setPageSize(nextPageSize);
      }
    },
    [pageSize]
  );

  const handleEdit = useCallback((budget) => {
    setEditDrawer({ open: true, budget });
  }, []);

  const closeEditDrawer = useCallback(() => {
    setEditDrawer({ open: false, budget: null });
  }, []);

  const handleOpenOdtForm = useCallback(async (budget) => {
    try {
      const summary = await getBudgetSummaryApi(budget.idBudget);
      if (!summary || summary.state === 'error' || !summary.id) {
        message.error(summary?.message || 'No se pudo cargar el presupuesto');
        return;
      }
      const idClient = summary.idClient ?? summary.cliente?.id ?? null;

      if (summary.order) {
        const orderRes = await getOrderWithItemsApi(summary.order.id);
        if (orderRes?.state !== 'success') {
          message.error(orderRes?.message || 'No se pudo cargar la ODT');
          return;
        }
        // Se propaga el objeto completo que devuelve getOrderWithItemsApi (trae
        // fecha_comprometida, address, commune, etc.) en vez de repetir una
        // lista blanca escrita a mano: esa lista fue la causa de la regresión
        // del 2026-07-22 (se armaba sin fecha_comprometida y el drawer, tras
        // sacarle la guarda que la protegía, mandaba `null` en cada guardado).
        // `id` y `assigned_to_user_id` se agregan aparte porque orderRes.order
        // no los trae (ese endpoint expone `id_order`, no `id`, y no incluye el
        // técnico asignado): ambos vienen del summary del presupuesto, que sí
        // los expone (bajo `assigned_to_user_id`, no `assigned_to` — defecto
        // hermano confirmado: el drawer siempre leía undefined y borraba el
        // técnico asignado en cada guardado).
        setOrderDrawer({
          open: true,
          budget: { id: summary.id, status: summary.status },
          idClient,
          hallazgos: summary.hallazgos || [],
          presupuestoItems: [],
          existingOrder: {
            ...(orderRes.order || {}),
            id: summary.order.id,
            assigned_to_user_id: summary.order.assigned_to_user_id ?? null,
          },
        });
        return;
      }

      setOrderDrawer({
        open: true,
        budget: { id: summary.id, status: summary.status },
        idClient,
        hallazgos: summary.hallazgos || [],
        presupuestoItems: summary.items || [],
        existingOrder: null,
      });
    } catch (err) {
      message.error(err?.message || 'Error al cargar el presupuesto');
    }
  }, []);

  // La lista no trae el id de la ODT: se resuelve con el summary del presupuesto.
  const handleOpenGantt = useCallback(
    async (budget) => {
      try {
        const summary = await getBudgetSummaryApi(budget.idBudget);
        if (!summary || summary.state === 'error') {
          message.error(summary?.message || 'No se pudo cargar el presupuesto');
          return;
        }
        if (!summary.order?.id) {
          message.warning('Este presupuesto aún no tiene ODT');
          return;
        }
        navigate(paths.admin.odt.gantt(summary.order.id));
      } catch (err) {
        message.error(err?.message || 'Error al abrir la carta Gantt');
      }
    },
    [navigate]
  );

  const closeOrderDrawer = useCallback(() => {
    setOrderDrawer(EMPTY_ORDER_DRAWER);
  }, []);

  const handleOpenOdtStatus = useCallback((budget) => {
    setStatusModal({ open: true, budget });
  }, []);

  const closeStatusModal = useCallback(() => {
    setStatusModal({ open: false, budget: null });
  }, []);

  const handleDownloadOdt = useCallback((budget) => downloadOdt(budget.idBudget), [downloadOdt]);

  const hasActiveFilters =
    filters.stage.length > 0 || !!filters.search || !!filters.idClient || !!filters.commune;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: token.marginLG,
        paddingTop: token.paddingMD,
        paddingBottom: token.paddingXL,
      }}
    >
      <PageHeader
        icon={FileTextOutlined}
        title="Gestión de Presupuestos"
        subtitle="Administra y visualiza todos los presupuestos del sistema"
        actions={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={bumpAndRefetch}>
              Actualizar
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setEditDrawer({ open: true, budget: null })}
            >
              Nuevo Presupuesto
            </Button>
          </Space>
        }
      />

      <SectionHint
        id="budget-list"
        message="Paso 2 del ciclo — presupuestar"
        description="Estos son los presupuestos generados desde los hallazgos. Aprobalos para generar la orden de trabajo, o vuelve a cotizar los rechazados."
      />

      <BudgetKpiStrip
        activeStages={filters.stage}
        refreshKey={refreshKey}
        onFilter={(group) => handleFiltersChange({ ...filters, stage: [...group.stages] })}
      />

      <BudgetToolbar filters={filters} onChange={handleFiltersChange} />

      {!loading && rows.length === 0 && !hasActiveFilters ? (
        <CicloEmpty
          title="Todavía no hay presupuestos"
          description="Los presupuestos nacen de los hallazgos detectados en las mantenciones. Andá a Hallazgos, seleccioná los que querés cotizar y presioná Presupuestar."
          ctaLabel="Ir a Hallazgos"
          ctaTo={paths.admin.hallazgos}
        />
      ) : (
        <BudgetTable
          rows={rows}
          total={total}
          loading={loading}
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onEdit={handleEdit}
          onApprove={setApprovalTarget}
          onOpenOdtForm={handleOpenOdtForm}
          onOpenOdtStatus={handleOpenOdtStatus}
          onDownloadOdt={handleDownloadOdt}
          onOpenGantt={handleOpenGantt}
          refetch={bumpAndRefetch}
        />
      )}

      <BudgetEditorDrawer
        open={editDrawer.open}
        budget={editDrawer.budget}
        onClose={closeEditDrawer}
        onSuccess={() => {
          closeEditDrawer();
          bumpAndRefetch();
        }}
      />

      <BudgetApprovalModal
        target={approvalTarget}
        onClose={() => setApprovalTarget(null)}
        onSuccess={(result) => {
          bumpAndRefetch();
          // Camino principal de creación de ODT: el modal entrega el id solo si creó una.
          if (result?.idOrder) setPlan({ open: true, idOrder: result.idOrder });
        }}
      />

      <GenerateOrderDrawer
        open={orderDrawer.open}
        budget={orderDrawer.budget}
        idClient={orderDrawer.idClient}
        hallazgos={orderDrawer.hallazgos}
        presupuestoItems={orderDrawer.presupuestoItems}
        existingOrder={orderDrawer.existingOrder}
        onClose={closeOrderDrawer}
        onSuccess={(result) => {
          // Solo al crear: editar una ODT existente no vuelve a preguntar por la
          // planificación. `existingOrder` se lee antes de cerrar el drawer.
          const fueCreacion = !orderDrawer.existingOrder;
          closeOrderDrawer();
          bumpAndRefetch();
          if (!fueCreacion || !result?.idOrder) return;
          setPlan({ open: true, idOrder: result.idOrder });
        }}
      />

      <PlanificarOdtModal
        open={plan.open}
        idOrder={plan.idOrder}
        onClose={() => setPlan((p) => ({ ...p, open: false }))}
        onPlan={() => {
          setPlan((p) => ({ ...p, open: false }));
          navigate(paths.admin.odt.gantt(plan.idOrder));
        }}
      />

      <Modal
        open={statusModal.open}
        onCancel={closeStatusModal}
        footer={false}
        title="Estado Orden de Trabajo"
        width={1000}
        destroyOnHidden
      >
        {statusModal.budget && <OrderStatus budget={statusModal.budget} />}
      </Modal>
    </div>
  );
}

export default Budget;
